import { useId, useState, type FormEvent } from 'react';
import { useSWRConfig } from 'swr';

import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { CURRENT_CONTEXT_KEY, useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import {
  addMemberByEmail,
  removeMember,
  setHouseholdName,
  setMemberRole,
  upgradeToFamily,
  type FamilyMember,
} from '../data/familyRepository.js';
import { useMembers } from '../hooks/useMembers.js';
import styles from './FamilyPage.module.css';

/**
 * "Gia đình" — the screen behind decision A1 (plan step 1.8, spec 22.3).
 *
 * Until the owner creates a family, this app has no household in it as far as the person can
 * tell: one space, one set of figures. This screen is the single door from there to a shared
 * space, which is why the tab is always in the navigation even before there is a family.
 *
 * Only an owner can change anything here. A member sees who is in the household and nothing
 * they could accidentally break.
 */
export function FamilyPage() {
  const contextState = useCurrentContext();
  const householdId = contextState.status === 'ready' ? contextState.context.household.id : null;
  const membersState = useMembers(
    contextState.status === 'ready' && contextState.context.isFamily ? householdId : null,
  );

  if (contextState.status === 'loading') return <LoadingState label="Đang tải…" />;
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }

  const { context } = contextState;

  if (!context.isFamily) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Gia đình</h1>
        <CreateFamilyCard householdId={context.household.id} />
      </div>
    );
  }

  const self =
    membersState.status === 'ready'
      ? membersState.members.find((member) => member.isSelf)
      : undefined;
  const isOwner = self?.role === 'owner';

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Gia đình</h1>

      <HouseholdNameCard
        householdId={context.household.id}
        name={context.household.name}
        canEdit={isOwner}
      />

      {membersState.status === 'loading' ? <LoadingState label="Đang tải thành viên…" /> : null}
      {membersState.status === 'error' ? (
        <ErrorState message={membersState.message} onRetry={membersState.retry} />
      ) : null}
      {membersState.status === 'ready' ? (
        <>
          <MemberList
            householdId={context.household.id}
            members={membersState.members}
            isOwner={isOwner}
            onChanged={() => void membersState.refresh()}
          />
          {isOwner ? (
            <AddMemberCard
              householdId={context.household.id}
              onAdded={() => void membersState.refresh()}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * The one-way step. It is spelled out rather than hidden behind a confirm dialog: there is no
 * RPC that turns a family back into a personal space, and the person deserves to know that
 * before they tap, not after.
 */
function CreateFamilyCard({ householdId }: { householdId: string }) {
  const { mutate } = useSWRConfig();
  const nameId = useId();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (name.trim() === '') {
      setError('Đặt tên cho gia đình trước đã.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await upgradeToFamily(householdId, name.trim());
      await mutate(CURRENT_CONTEXT_KEY);
    } catch (caught) {
      setError(toAppError(caught).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={lists.card}>
      <h2 className={lists.cardTitle}>Tạo gia đình</h2>
      <p className={styles.lead}>
        Khi tạo gia đình, bạn có thể mời vợ/chồng cùng ghi chép và xem báo cáo chung. Số liệu cá
        nhân của bạn vẫn giữ nguyên — báo cáo mặc định vẫn là của riêng bạn.
      </p>
      <p className={styles.warning}>Đã tạo gia đình thì không quay lại không gian cá nhân được.</p>

      <form className={forms.form} onSubmit={(event) => void submit(event)}>
        <div className={forms.field}>
          <label htmlFor={nameId}>Tên gia đình</label>
          <input
            id={nameId}
            className={forms.input}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Nhà mình"
            autoComplete="off"
          />
        </div>

        {error ? (
          <p className={forms.error} role="alert">
            {error}
          </p>
        ) : null}

        <div className={forms.actions}>
          <button type="submit" className={forms.primary} disabled={busy}>
            {busy ? 'Đang tạo…' : 'Tạo gia đình'}
          </button>
        </div>
      </form>
    </section>
  );
}

function HouseholdNameCard({
  householdId,
  name,
  canEdit,
}: {
  householdId: string;
  name: string;
  canEdit: boolean;
}) {
  const { mutate } = useSWRConfig();
  const nameId = useId();
  const [value, setValue] = useState(name);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canEdit) {
    return (
      <section className={lists.card}>
        <h2 className={lists.cardTitle}>Tên gia đình</h2>
        <p className={styles.lead}>{name}</p>
      </section>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      await setHouseholdName(householdId, value.trim());
      await mutate(CURRENT_CONTEXT_KEY);
      setNotice('Đã đổi tên gia đình.');
    } catch (caught) {
      setError(toAppError(caught).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={lists.card}>
      <h2 className={lists.cardTitle}>Tên gia đình</h2>
      <form className={forms.form} onSubmit={(event) => void submit(event)}>
        <div className={forms.field}>
          <label htmlFor={nameId}>Tên gia đình</label>
          <input
            id={nameId}
            className={forms.input}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoComplete="off"
          />
        </div>

        {notice ? (
          <p className={forms.success} role="status">
            {notice}
          </p>
        ) : null}
        {error ? (
          <p className={forms.error} role="alert">
            {error}
          </p>
        ) : null}

        <div className={forms.actions}>
          <button type="submit" className={forms.primary} disabled={busy}>
            Lưu tên
          </button>
        </div>
      </form>
    </section>
  );
}

const ROLE_LABELS: Record<FamilyMember['role'], string> = {
  owner: 'Chủ hộ',
  member: 'Thành viên',
};

function MemberList({
  householdId,
  members,
  isOwner,
  onChanged,
}: {
  householdId: string;
  members: readonly FamilyMember[];
  isOwner: boolean;
  onChanged: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(userId: string, action: () => Promise<void>) {
    setBusyId(userId);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (caught) {
      setError(toAppError(caught).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className={lists.card} aria-labelledby="members-heading">
      <h2 className={lists.cardTitle} id="members-heading">
        Thành viên
      </h2>

      {error ? (
        <p className={forms.error} role="alert">
          {error}
        </p>
      ) : null}

      <ul className={lists.list}>
        {members.map((member) => (
          <li key={member.userId}>
            <div className={lists.row}>
              <span className={lists.rowText}>
                <span className={lists.rowName}>
                  {member.displayName}
                  {member.isSelf ? ' (bạn)' : ''}
                </span>
                <span className={lists.rowMeta}>{ROLE_LABELS[member.role]}</span>
              </span>

              {/* An owner cannot remove themselves (the RPC refuses too): a household with
                  nobody in charge could never be administered again. */}
              {isOwner && !member.isSelf ? (
                <span className={lists.rowActions}>
                  <button
                    type="button"
                    className={lists.rowButton}
                    disabled={busyId === member.userId}
                    onClick={() =>
                      void run(member.userId, () =>
                        setMemberRole(
                          householdId,
                          member.userId,
                          member.role === 'owner' ? 'member' : 'owner',
                        ),
                      )
                    }
                  >
                    {member.role === 'owner' ? 'Bỏ quyền chủ hộ' : 'Đặt làm chủ hộ'}
                  </button>
                  <button
                    type="button"
                    className={lists.rowButtonDanger}
                    disabled={busyId === member.userId}
                    onClick={() => {
                      const ok = window.confirm(
                        `Xoá ${member.displayName} khỏi gia đình? Các khoản đã ghi vẫn giữ nguyên.`,
                      );
                      if (ok)
                        void run(member.userId, () => removeMember(householdId, member.userId));
                    }}
                  >
                    Xoá
                  </button>
                </span>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * There is no public signup (decision B7): the account is created in Supabase first, and
 * this screen only connects an existing one to the household.
 */
function AddMemberCard({ householdId, onAdded }: { householdId: string; onAdded: () => void }) {
  const emailId = useId();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      await addMemberByEmail(householdId, email.trim());
      setEmail('');
      setNotice('Đã thêm thành viên.');
      onAdded();
    } catch (caught) {
      setError(toAppError(caught).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={lists.card}>
      <h2 className={lists.cardTitle}>Thêm thành viên</h2>
      <p className={styles.lead}>
        Người đó cần có sẵn tài khoản đăng nhập. Nhập đúng email họ dùng để đăng nhập.
      </p>

      <form className={forms.form} onSubmit={(event) => void submit(event)}>
        <div className={forms.field}>
          <label htmlFor={emailId}>Email thành viên</label>
          <input
            id={emailId}
            className={forms.input}
            type="email"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="off"
          />
        </div>

        {notice ? (
          <p className={forms.success} role="status">
            {notice}
          </p>
        ) : null}
        {error ? (
          <p className={forms.error} role="alert">
            {error}
          </p>
        ) : null}

        <div className={forms.actions}>
          <button type="submit" className={forms.primary} disabled={busy || email.trim() === ''}>
            Thêm thành viên
          </button>
        </div>
      </form>
    </section>
  );
}
